import { v4 as uuidv4 } from 'uuid';
import { ValidationError, BookingTypeNotAllowedError } from '../utils/errors.js';
import { Rink } from './Rink.js';

/**
 * Represents a bowling green containing multiple rinks
 */
export class Green {
  /**
   * Create a new Green
   * @param {Object} data - Green configuration
   * @param {string} [data._id] - Unique identifier (auto-generated if not provided)
   * @param {string} data.name - Name of the green
   * @param {Array<Rink>} [data.rinks] - Array of Rink instances (max 12)
   * @param {string} [data.location] - Physical location/address
   * @param {Array<string>} [data.facilities] - Array of available facilities
   * @param {Array<string>} [data.allowedBookingTypes] - Array of BookingType IDs
   */
  constructor(data) {
    if (!data || typeof data !== 'object') {
      throw new ValidationError('Green data is required');
    }

    if (!data.name || typeof data.name !== 'string') {
      throw new ValidationError('Name is required', 'name', data.name);
    }

    this._id = data._id || uuidv4();
    this.name = data.name.trim();
    this.location = data.location ? data.location.trim() : '';
    this.facilities = Array.isArray(data.facilities) ? [...data.facilities] : [];
    this.allowedBookingTypes = Array.isArray(data.allowedBookingTypes) 
      ? [...data.allowedBookingTypes] 
      : [];

    // Initialize rinks
    this.rinks = [];
    if (Array.isArray(data.rinks)) {
      for (const rinkData of data.rinks) {
        if (rinkData instanceof Rink) {
          this.addRink(rinkData);
        } else {
          this.addRink(new Rink(rinkData));
        }
      }
    }
  }

  /**
   * Add a rink to the green (max 12)
   * @param {Rink|Object} rink - Rink instance or rink data
   * @returns {Green} This instance for chaining
   */
  addRink(rink) {
    if (this.rinks.length >= 12) {
      throw new ValidationError('Maximum of 12 rinks allowed per green', 'rinks', this.rinks.length);
    }

    let rinkInstance;
    if (rink instanceof Rink) {
      rinkInstance = rink;
    } else if (rink && typeof rink === 'object') {
      rinkInstance = new Rink(rink);
    } else {
      throw new ValidationError('Valid rink data or Rink instance is required', 'rink', rink);
    }

    // Check for duplicate rink numbers
    const existingRink = this.rinks.find(r => r.number === rinkInstance.number);
    if (existingRink) {
      throw new ValidationError(
        `Rink number ${rinkInstance.number} already exists`,
        'rinkNumber',
        rinkInstance.number
      );
    }

    this.rinks.push(rinkInstance);
    return this;
  }

  /**
   * Remove a rink from the green
   * @param {string} rinkId - Rink ID to remove
   * @returns {Green} This instance for chaining
   */
  removeRink(rinkId) {
    if (!rinkId || typeof rinkId !== 'string') {
      throw new ValidationError('Valid rink ID is required', 'rinkId', rinkId);
    }

    const index = this.rinks.findIndex(rink => rink._id === rinkId);
    if (index === -1) {
      throw new ValidationError('Rink not found', 'rinkId', rinkId);
    }

    this.rinks.splice(index, 1);
    return this;
  }

  /**
   * Get a specific rink by ID
   * @param {string} rinkId - Rink ID to find
   * @returns {Rink|null} Rink instance or null if not found
   */
  getRink(rinkId) {
    if (!rinkId || typeof rinkId !== 'string') {
      return null;
    }

    return this.rinks.find(rink => rink._id === rinkId) || null;
  }

  /**
   * Get a rink by number
   * @param {number} rinkNumber - Rink number to find
   * @returns {Rink|null} Rink instance or null if not found
   */
  getRinkByNumber(rinkNumber) {
    if (typeof rinkNumber !== 'number') {
      return null;
    }

    return this.rinks.find(rink => rink.number === rinkNumber) || null;
  }

  /**
   * Get available rinks for time period
   * @param {Date} date - Date to check
   * @param {Date} start - Start time
   * @param {Date} end - End time
   * @param {Array} existingBookings - Existing bookings to check against
   * @param {string} bookingTypeId - Booking type ID to check restrictions
   * @param {string} excludeBookingId - Booking ID to exclude from check
   * @returns {Array<Rink>} Available rinks
   */
  getAvailableRinks(date, start, end, existingBookings = [], bookingTypeId = null, excludeBookingId = null) {
    if (!date || !start || !end) {
      return [];
    }

    return this.rinks.filter(rink => {
      // Check booking type restrictions
      if (bookingTypeId && !this.isBookingTypeAllowed(bookingTypeId)) {
        return false;
      }
      
      if (bookingTypeId && !rink.isBookingTypeAllowed(bookingTypeId)) {
        return false;
      }

      // Check availability
      return rink.isAvailable(date, start, end, existingBookings, excludeBookingId);
    }).sort((a, b) => a.number - b.number);
  }

  /**
   * Add a booking type to allowed types
   * @param {string} bookingTypeId - Booking type ID to add
   * @returns {Green} This instance for chaining
   */
  addAllowedBookingType(bookingTypeId) {
    if (!bookingTypeId || typeof bookingTypeId !== 'string') {
      throw new ValidationError('Valid booking type ID is required', 'bookingTypeId', bookingTypeId);
    }

    if (!this.allowedBookingTypes.includes(bookingTypeId)) {
      this.allowedBookingTypes.push(bookingTypeId);
    }

    return this;
  }

  /**
   * Remove a booking type from allowed types
   * @param {string} bookingTypeId - Booking type ID to remove
   * @returns {Green} This instance for chaining
   */
  removeAllowedBookingType(bookingTypeId) {
    if (!bookingTypeId || typeof bookingTypeId !== 'string') {
      throw new ValidationError('Valid booking type ID is required', 'bookingTypeId', bookingTypeId);
    }

    const index = this.allowedBookingTypes.indexOf(bookingTypeId);
    if (index > -1) {
      this.allowedBookingTypes.splice(index, 1);
    }

    return this;
  }

  /**
   * Check if booking type is allowed on this green
   * @param {string} bookingTypeId - Booking type ID to check
   * @returns {boolean} True if booking type is allowed
   */
  isBookingTypeAllowed(bookingTypeId) {
    if (!bookingTypeId || typeof bookingTypeId !== 'string') {
      return false;
    }

    // If no restrictions, allow all booking types
    if (this.allowedBookingTypes.length === 0) {
      return true;
    }

    return this.allowedBookingTypes.includes(bookingTypeId);
  }

  /**
   * Add a facility to the green
   * @param {string} facility - Facility name to add
   * @returns {Green} This instance for chaining
   */
  addFacility(facility) {
    if (!facility || typeof facility !== 'string') {
      throw new ValidationError('Valid facility name is required', 'facility', facility);
    }

    const facilityName = facility.trim();
    if (!this.facilities.includes(facilityName)) {
      this.facilities.push(facilityName);
    }

    return this;
  }

  /**
   * Remove a facility from the green
   * @param {string} facility - Facility name to remove
   * @returns {Green} This instance for chaining
   */
  removeFacility(facility) {
    if (!facility || typeof facility !== 'string') {
      throw new ValidationError('Valid facility name is required', 'facility', facility);
    }

    const index = this.facilities.indexOf(facility.trim());
    if (index > -1) {
      this.facilities.splice(index, 1);
    }

    return this;
  }

  /**
   * Update green details
   * @param {Object} updates - Properties to update
   * @returns {Green} This instance for chaining
   */
  update(updates) {
    if (!updates || typeof updates !== 'object') {
      throw new ValidationError('Updates object is required');
    }

    const allowedFields = ['name', 'location', 'facilities', 'allowedBookingTypes'];
    const updateKeys = Object.keys(updates);

    for (const key of updateKeys) {
      if (!allowedFields.includes(key)) {
        throw new ValidationError(`Field '${key}' cannot be updated`, key, updates[key]);
      }
    }

    // Apply updates
    if (updates.name !== undefined) {
      if (!updates.name || typeof updates.name !== 'string') {
        throw new ValidationError('Name must be a non-empty string', 'name', updates.name);
      }
      this.name = updates.name.trim();
    }

    if (updates.location !== undefined) {
      this.location = updates.location ? updates.location.trim() : '';
    }

    if (updates.facilities !== undefined) {
      if (!Array.isArray(updates.facilities)) {
        throw new ValidationError('Facilities must be an array', 'facilities', updates.facilities);
      }
      this.facilities = [...updates.facilities];
    }

    if (updates.allowedBookingTypes !== undefined) {
      if (!Array.isArray(updates.allowedBookingTypes)) {
        throw new ValidationError('Allowed booking types must be an array', 'allowedBookingTypes', updates.allowedBookingTypes);
      }
      this.allowedBookingTypes = [...updates.allowedBookingTypes];
    }

    return this;
  }

  /**
   * Get bookings for this green within a date range
   * @param {Array} allBookings - All bookings to filter
   * @param {Date} startDate - Start of date range
   * @param {Date} endDate - End of date range
   * @returns {Array} Filtered bookings for this green
   */
  getBookings(allBookings, startDate = null, endDate = null) {
    const rinkIds = this.rinks.map(rink => rink._id);
    let greenBookings = allBookings.filter(booking => rinkIds.includes(booking.rinkId));

    if (startDate && endDate) {
      greenBookings = greenBookings.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    }

    return greenBookings.sort((a, b) => a.start - b.start);
  }

  /**
   * Get utilization statistics for this green
   * @param {Array} allBookings - All bookings to analyze
   * @param {Date} startDate - Start of analysis period
   * @param {Date} endDate - End of analysis period
   * @returns {Object} Utilization statistics
   */
  getUtilizationStats(allBookings, startDate, endDate) {
    const greenBookings = this.getBookings(allBookings, startDate, endDate)
      .filter(booking => booking.status === 'confirmed' || booking.status === 'completed');

    const totalMinutes = greenBookings.reduce((sum, booking) => {
      return sum + Math.round((booking.end - booking.start) / (1000 * 60));
    }, 0);

    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const totalAvailableMinutes = totalDays * this.rinks.length * 12 * 60; // Assuming 12 hours per day per rink

    const rinkStats = this.rinks.map(rink => 
      rink.getUtilizationStats(allBookings, startDate, endDate)
    );

    return {
      greenId: this._id,
      greenName: this.name,
      totalRinks: this.rinks.length,
      totalBookings: greenBookings.length,
      totalBookedMinutes: totalMinutes,
      totalAvailableMinutes,
      utilizationPercentage: (totalMinutes / totalAvailableMinutes) * 100,
      averageBookingDuration: greenBookings.length > 0 ? totalMinutes / greenBookings.length : 0,
      rinkStats
    };
  }

  /**
   * Validate if a booking can be made on this green
   * @param {string} rinkId - Rink ID
   * @param {string} bookingTypeId - Booking type ID
   * @param {Date} date - Booking date
   * @param {Date} start - Start time
   * @param {Date} end - End time
   * @param {Array} existingBookings - Existing bookings
   * @param {string} excludeBookingId - Booking ID to exclude
   * @throws {ValidationError} When rink is not found
   * @throws {BookingTypeNotAllowedError} When booking type is not allowed
   */
  validateBooking(rinkId, bookingTypeId, date, start, end, existingBookings = [], excludeBookingId = null) {
    // Check if rink exists
    const rink = this.getRink(rinkId);
    if (!rink) {
      throw new ValidationError(`Rink ${rinkId} not found in green ${this.name}`, 'rinkId', rinkId);
    }

    // Check if booking type is allowed on green
    if (!this.isBookingTypeAllowed(bookingTypeId)) {
      throw new BookingTypeNotAllowedError(
        `Booking type ${bookingTypeId} is not allowed on green ${this.name}`,
        bookingTypeId,
        `green-${this._id}`
      );
    }

    // Delegate to rink validation
    rink.validateBooking(bookingTypeId, date, start, end, existingBookings, excludeBookingId);
  }

  /**
   * Convert to JSON string for persistence
   * @returns {string} JSON string representation
   */
  toJSON() {
    const data = {
      _id: this._id,
      name: this.name,
      location: this.location,
      facilities: [...this.facilities],
      allowedBookingTypes: [...this.allowedBookingTypes],
      rinks: this.rinks.map(rink => JSON.parse(rink.toJSON()))
    };
    return JSON.stringify(data);
  }

  /**
   * Create Green instance from JSON string
   * @param {string} jsonString - JSON string data
   * @returns {Green} New Green instance
   */
  static fromJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') {
      throw new ValidationError('Valid JSON string is required');
    }

    let jsonData;
    try {
      jsonData = JSON.parse(jsonString);
    } catch (error) {
      throw new ValidationError('Invalid JSON string format');
    }

    return new Green({
      _id: jsonData._id,
      name: jsonData.name,
      location: jsonData.location,
      facilities: jsonData.facilities || [],
      allowedBookingTypes: jsonData.allowedBookingTypes || [],
      rinks: jsonData.rinks || []
    });
  }
} 